/**
 * useReport Hook
 * 
 * Fetches and manages a single report by ID.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Report } from '../types';
import { getApiUrl } from '../config/environments';

/**
 * Create axios instance with auth token interceptor
 */
const createAuthenticatedAxios = (getToken: () => Promise<string | null>) => {
  const instance = axios.create({
    baseURL: getApiUrl()
  });

  // Add request interceptor to attach auth token
  instance.interceptors.request.use(
    async (config) => {
      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  return instance;
};

interface UseReportReturn {
  report: Report | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useReport(reportId: string | null): UseReportReturn {
  const { getToken } = useAuth();
  const [report, setReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Create authenticated axios instance
  const axiosInstance = useRef(createAuthenticatedAxios(getToken));

  const fetchReport = useCallback(async () => {
    if (!reportId) {
      setReport(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.current.get(`/reports/${reportId}`);
      
      console.log('📊 Report response received:', {
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        fullData: response.data
      });
      
      // Transform snake_case to camelCase
      const result = response.data;
      const fetchedReport: Report = {
        id: result.id,
        assessmentId: result.assessment_id,
        title: result.title || 'Untitled Report',
        executiveSummary: result.executive_summary || '',
        keyFindings: result.key_findings || [],
        recommendations: result.recommendations || [],
        sections: (result.sections || []).map((section: any) => ({
          title: section.title || '',
          content: section.content || '',
          order: section.order || 0
        })),
        diagrams: (result.diagrams || []).map((diagram: any) => ({
          title: diagram.title || '',
          description: diagram.description || '',
          mermaidCode: diagram.mermaid_code || '',
          processType: diagram.process_type || 'flowchart',
          order: diagram.order || 0
        })),
        generatedAt: result.generated_at || new Date().toISOString(),
        status: result.status || 'completed',

        // Campos adicionales (transformados de snake_case a camelCase)
        businessIntelligence: result.business_intelligence || undefined,
        roiCalculation: result.roi_calculation || undefined,
        implementationRoadmap: result.implementation_roadmap || undefined
      };
      
      console.log('✅ Report transformed:', {
        id: fetchedReport.id,
        title: fetchedReport.title,
        hasExecutiveSummary: !!fetchedReport.executiveSummary,
        keyFindingsCount: fetchedReport.keyFindings.length,
        recommendationsCount: fetchedReport.recommendations.length,
        sectionsCount: fetchedReport.sections.length,
        diagramsCount: fetchedReport.diagrams.length
      });
      
      setReport(fetchedReport);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch report';
      const error = new Error(errorMessage);
      setError(error);
      console.error('Error fetching report:', error);
    } finally {
      setIsLoading(false);
    }
  }, [reportId, getToken]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    report,
    isLoading,
    error,
    refetch: fetchReport
  };
}

